/**
 * What lets the sync queue send one write twice.
 *
 * A reload can abandon a request after the server has already committed it, so
 * the queue sends it again. That is safe only when the server can tell the
 * second send from a new one: a create carries a key it dedupes on, and an
 * update carries the version its edit was based on, which the server refuses
 * once another write has moved the row on.
 */

/*
 * Old data
 *
 * A device keeps its IndexedDB across an app update, so the first build with
 * replay protection meets rows an earlier build wrote. Four paths exist only
 * for them, and nothing a current build writes takes any of them:
 *
 *  1. A versioned queue row with no `base_version` is based conservatively on
 *     version zero for an existing row, or on no row for an answer or
 *     answer-risk create. A newer server version refuses the write.
 *  2. A `processing` claim with no `replay_protected` stamp keeps the long
 *     lease (`UNPROTECTED_CLAIM_STALE_AFTER`): nothing says that build's
 *     request was safe to repeat.
 *  3. An edit of a row that has a server id and no stored `version` queues no
 *     base (`baseVersionFor` in db.ts), because none is known. The response
 *     stores the version, so that row's next edit is versioned. For answers
 *     and answer-risks with no server id, an edit uses the "no row yet" base;
 *     a server row created by an unconfirmed send refuses it. Risk and
 *     inspection edits use the version their confirmed create returns.
 *  4. A queue row with no `revision` is read as revision 0 when the next edit
 *     of its record is queued.
 *
 * An inspection create queued by an earlier build may already be on the
 * server, even when the queue records no attempt: the response can disappear
 * after the server commits. It carried no stable key, so the current build
 * holds the local row for review instead of sending it again. The
 * `writtenBySchema` marker distinguishes those queue rows from current ones;
 * a `replay_protected` claim stamp alone does not establish their origin.
 *
 * Every response and every pull now stores a row's version, and new claims are
 * stamped. The held old inspection creates need support reconciliation; other
 * old rows drain as they are sent, pulled or retired. Tests seeded from the
 * old shapes pin them
 * (`sync-engine.test.ts` → "rows an earlier build left on the device",
 * `db.test.ts` → "rows an earlier build left on the device").
 *
 * Remove the compatibility paths with those tests only after the supported
 * upgrade policy and telemetry show that unmarked rows no longer need support.
 */

/** Queue tables whose update-style writes name the server version they were based on. */
export const VERSIONED_TABLES = [
  "inspections",
  "inspection_answers",
  "inspection_answer_risks",
  "inspection_risks",
  "risk_instances",
] as const;

export type VersionedTable = (typeof VERSIONED_TABLES)[number];

export const isVersionedTable = (table: string): table is VersionedTable =>
  (VERSIONED_TABLES as readonly string[]).includes(table);

/**
 * The base of a create of an answer or an answer-risk: "there is no row yet".
 * Row versions start at 0, so no stored version equals it, and the server
 * refuses the write whenever the upsert finds a row. A second create of the
 * same new answer, from this device or another, therefore cannot overwrite the
 * first; a replay of a create that landed is refused too, and the client then
 * reads the stored row to see whether it already holds what was sent.
 */
export const NEW_ROW_BASE_VERSION = -1;

interface QueuedWrite {
  readonly table_name: string;
  readonly operation: "create" | "update" | "delete";
  readonly data?: {
    readonly base_version?: number;
  } | null;
}

/**
 * Whether the server can recognise a second send of this write. Decided when
 * the write is claimed, from what this client will send: a claim left behind
 * by an earlier app version carries no such stamp and keeps the long lease,
 * because nothing says that app's request was protected.
 *
 * Deletes are safe to repeat: the row is gone the second time, which the
 * queue settles as done. The cleanup of an answer whose create may have
 * landed unconfirmed first creates the answer as based on no row to learn its
 * id; a second send of that create is refused with the row the server holds,
 * so it overwrites nothing either. Measure-template updates are dispatched by
 * the engine but nothing enqueues them, so they are not claimed as protected.
 */
export const willSendReplayProtected = (write: QueuedWrite): boolean => {
  if (write.operation === "delete") return true;
  switch (write.table_name) {
    case "inspections":
    case "risk_instances":
      // A create sends its local id as the client key; an update sends a base
      // version when the row had one.
      return write.operation === "create" || hasBaseVersion(write);
    case "inspection_answers":
    case "inspection_answer_risks":
    case "inspection_risks":
      return hasBaseVersion(write);
    case "inspection_questionnaire_links":
    case "risk_instance_attachments":
    case "measures":
      return write.operation === "create";
    default:
      return false;
  }
};

const hasBaseVersion = (write: QueuedWrite): boolean =>
  typeof write.data?.base_version === "number";

/** Business fields compared when the server refuses a write, per table. */
const COMPARED_FIELDS: Record<VersionedTable, readonly string[]> = {
  inspections: [
    "location_id",
    "inspection_date",
    "inspection_end_date",
    "type",
    "reason",
    "status",
    "contributors",
    "report_recipients",
    "metadata",
    "start_time",
    "end_time",
  ],
  inspection_answers: ["answer", "questionnaire_translation_group_id"],
  inspection_answer_risks: ["comment", "photos"],
  inspection_risks: ["comment", "photos"],
  risk_instances: [
    "title",
    "description",
    "location_id",
    "is_company_wide",
    "severity",
    "probability",
    "status",
    "accepted_by",
    "rejected_by",
    "rejection_reason",
  ],
};

const isoOrSame = (value: unknown): unknown => {
  if (value instanceof Date) return value.toISOString();
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    const time = Date.parse(value);
    return Number.isNaN(time) ? value : new Date(time).toISOString();
  }
  return value;
};

const normalise = (value: unknown): unknown => {
  const converted = isoOrSame(value);
  if (converted === undefined || converted === null) return null;
  if (Array.isArray(converted)) return converted.map(normalise);
  if (typeof converted === "object") {
    return Object.fromEntries(
      Object.entries(converted)
        .filter(([, entry]) => entry !== undefined)
        .sort(([a], [b]) => (a < b ? -1 : 1))
        .map(([key, entry]) => [key, normalise(entry)]),
    );
  }
  return converted;
};

// An inspection date round-trips as a calendar date or a full timestamp
// depending on who wrote it, so compare the calendar part of a date field.
const DATE_ONLY_FIELDS = new Set(["inspection_date", "inspection_end_date"]);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

// A photo link carries local bookkeeping (an offline photo id) the server never
// stores, and the server may fill in an attachment id the sender did not name.
// The server holds a link's file, its filename and its attachment, so a sent
// link matches a stored one when every one of those it names is held with the
// same value. A link that names none of them has no identity, so it is kept
// whole and equals only an identical link.
const PHOTO_KEYS = [
  "file_id",
  "filename",
  "risk_instance_attachment_id",
] as const;

const namedPhotoKeys = (photo: unknown): string[] =>
  isRecord(photo)
    ? PHOTO_KEYS.filter(
        (key) => photo[key] !== undefined && photo[key] !== null,
      )
    : [];

const photoMatches = (sent: unknown, stored: unknown): boolean => {
  const named = namedPhotoKeys(sent);
  if (named.length === 0) {
    return (
      JSON.stringify(normalise(sent)) === JSON.stringify(normalise(stored))
    );
  }
  // Present on the stored link and equal, type included: a missing field is not
  // the text it would print as.
  return (
    isRecord(sent) &&
    isRecord(stored) &&
    named.every((key) => stored[key] !== undefined && stored[key] === sent[key])
  );
};

// Each sent link needs a stored link of its own and no stored link may be left
// over: a complete matching, found by augmenting paths so that the order the
// links come in cannot decide the answer.
const samePhotos = (sent: unknown, stored: unknown): boolean => {
  const sentList = Array.isArray(sent) ? sent : [];
  const storedList = Array.isArray(stored) ? stored : [];
  if (sentList.length !== storedList.length) return false;
  const heldBy: number[] = storedList.map(() => -1);
  const assign = (photo: number, seen: Set<number>): boolean => {
    for (let slot = 0; slot < storedList.length; slot++) {
      if (seen.has(slot) || !photoMatches(sentList[photo], storedList[slot])) {
        continue;
      }
      seen.add(slot);
      const holder = heldBy[slot] ?? -1;
      if (holder === -1 || assign(holder, seen)) {
        heldBy[slot] = photo;
        return true;
      }
    }
    return false;
  };
  return sentList.every((_, photo) => assign(photo, new Set()));
};

// The wire schema keeps these keys of a contributor and drops the rest, so a
// local record carrying more compares by the keys the server can hold.
const CONTRIBUTOR_KEYS = [
  "id",
  "first_name",
  "last_name",
  "email",
  "role",
  "client_id",
  "company",
  "official_title",
] as const;
const RECIPIENT_KEYS = ["email", "name"] as const;

const onlyKeys = (value: unknown, keys: readonly string[]): unknown =>
  Array.isArray(value)
    ? value.map((entry) =>
        isRecord(entry)
          ? Object.fromEntries(
              keys.flatMap((key) => (key in entry ? [[key, entry[key]]] : [])),
            )
          : entry,
      )
    : value;

const sameField = (field: string, sent: unknown, stored: unknown): boolean => {
  if (field === "photos") return samePhotos(sent, stored);
  if (field === "contributors") {
    return (
      JSON.stringify(normalise(onlyKeys(sent, CONTRIBUTOR_KEYS))) ===
      JSON.stringify(normalise(onlyKeys(stored, CONTRIBUTOR_KEYS)))
    );
  }
  if (field === "report_recipients") {
    return (
      JSON.stringify(normalise(onlyKeys(sent, RECIPIENT_KEYS))) ===
      JSON.stringify(normalise(onlyKeys(stored, RECIPIENT_KEYS)))
    );
  }
  if (DATE_ONLY_FIELDS.has(field)) {
    const day = (value: unknown) => {
      const converted = isoOrSame(value);
      return typeof converted === "string"
        ? converted.slice(0, 10)
        : converted === undefined
          ? null
          : converted;
    };
    return JSON.stringify(day(sent)) === JSON.stringify(day(stored));
  }
  return JSON.stringify(normalise(sent)) === JSON.stringify(normalise(stored));
};

/**
 * Whether the row the server holds already carries every business field this
 * write would set. Only fields the write names are compared: a partial update
 * says nothing about the rest. A refused write that matches landed earlier (a
 * lost response); one that differs was overtaken by another writer.
 */
export const writeMatchesStoredRow = (
  table: VersionedTable,
  sent: Readonly<Record<string, unknown>>,
  stored: Readonly<Record<string, unknown>>,
): boolean =>
  COMPARED_FIELDS[table]
    .filter((field) => field in sent && sent[field] !== undefined)
    .every((field) => sameField(field, sent[field], stored[field]));

/**
 * Whether the inspection the server holds under a create's id is the one that
 * create made. Besides the fields an update can change, a create sets the
 * language and, when the device names one, the translation group; a missing
 * group is the server's to generate, so it says nothing.
 */
export const inspectionCreateMatchesStoredRow = (
  sent: Readonly<Record<string, unknown>>,
  stored: Readonly<Record<string, unknown>>,
): boolean =>
  writeMatchesStoredRow("inspections", sent, stored) &&
  ["language", "translation_group_id"]
    .filter((field) => sent[field] !== undefined && sent[field] !== null)
    .every((field) => sameField(field, sent[field], stored[field]));

/**
 * Where an inspection's queued create keeps the payload it first sent. The
 * server holds that content under the inspection's id, so every later send of
 * the create repeats it rather than the inspection as edited since.
 */
export const SENT_CREATE = "_sent_create";

export const withoutSentCreate = (
  data: Readonly<Record<string, unknown>>,
): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(data).filter(([key]) => key !== SENT_CREATE),
  );

/** Whether two wire payloads say the same thing, ignoring key order and date spelling. */
export const samePayload = (a: unknown, b: unknown): boolean =>
  JSON.stringify(normalise(a)) === JSON.stringify(normalise(b));

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Whether a local key is a UUID, so it can be the inspection's id. Every write
 * path mints its key with a UUID, so an inspection on a device is keyed by one;
 * a key of any other shape is replaced by a minted id before the create is sent.
 */
export const isDeviceMintedId = (key: unknown): key is string =>
  typeof key === "string" && UUID.test(key);

/**
 * Whether a create failed because the id the device minted for the inspection
 * belongs to another row: another client's, or this client's with a different
 * payload. The server wrote nothing and says nothing about that row.
 */
export const isInspectionIdTaken = (error: unknown): boolean => {
  const candidates = isRecord(error) ? [error, error["cause"]] : [error];
  return candidates.some(
    (candidate) =>
      isRecord(candidate) && candidate["_tag"] === "InspectionIdTaken",
  );
};

/**
 * Whether an update failed because the row it was based on no longer exists on
 * the server (the contract's `*Gone`).
 */
export const rowGoneOf = (error: unknown): boolean => {
  const candidates = isRecord(error) ? [error, error["cause"]] : [error];
  return candidates.some(
    (candidate) =>
      isRecord(candidate) &&
      typeof candidate["_tag"] === "string" &&
      candidate["_tag"].endsWith("Gone"),
  );
};

/** The typed conflict an update failure carries, if it is one. */
export const versionConflictOf = (
  error: unknown,
): { readonly current: Readonly<Record<string, unknown>> } | undefined => {
  const candidates = isRecord(error) ? [error, error["cause"]] : [error];
  for (const candidate of candidates) {
    if (
      isRecord(candidate) &&
      typeof candidate["_tag"] === "string" &&
      candidate["_tag"].endsWith("VersionConflict") &&
      isRecord(candidate["current"])
    ) {
      return { current: candidate["current"] };
    }
  }
  return undefined;
};
